/**
 * `cofactor status` — publish incidents to the self-hosted status page.
 *
 * Auth is pure AWS IAM: we assume the `status-publisher` role for short-term
 * credentials (1h) and SigV4-sign requests to the IAM-authed write Function
 * URL. No passwords, no API tokens of our own. Every command also accepts
 * flags / env vars so agents can drive it non-interactively.
 *
 * Config resolution (each value): --flag > env var > ~/.canary/status.json
 *   write URL : --write-url  | STATUS_WRITE_URL
 *   role ARN  : --role-arn   | STATUS_PUBLISHER_ROLE_ARN
 *   public URL: --public-url | STATUS_PUBLIC_URL          (for `list`)
 *   region    : --region     | AWS_REGION                 (default us-east-1)
 *   profile   : --profile    | AWS_PROFILE
 *
 * @module status
 */
import process from 'node:process';
import fs from 'node:fs';
import os from 'node:os';
import path from 'node:path';
import { getArgValue, hasFlag } from './auth.js';
import { amzDateNow, signRequest, type Sv4Credentials } from './status-sign.js';

const CONFIG_PATH = path.join(os.homedir(), '.canary', 'status.json');
const DEFAULT_REGION = 'us-east-1';

interface StatusConfig {
  writeUrl?: string;
  roleArn?: string;
  publicUrl?: string;
  region?: string;
}

// ── config ────────────────────────────────────────────────────────────────

function readConfigFile(): StatusConfig {
  try {
    return JSON.parse(fs.readFileSync(CONFIG_PATH, 'utf-8')) as StatusConfig;
  } catch {
    return {};
  }
}

function writeConfigFile(config: StatusConfig): void {
  fs.mkdirSync(path.dirname(CONFIG_PATH), { recursive: true });
  fs.writeFileSync(CONFIG_PATH, JSON.stringify(config, null, 2));
}

function resolveConfig(argv: string[]): Required<Pick<StatusConfig, 'region'>> & StatusConfig {
  const file = readConfigFile();
  return {
    writeUrl: getArgValue(argv, '--write-url') ?? process.env.STATUS_WRITE_URL ?? file.writeUrl,
    roleArn:
      getArgValue(argv, '--role-arn') ?? process.env.STATUS_PUBLISHER_ROLE_ARN ?? file.roleArn,
    publicUrl: getArgValue(argv, '--public-url') ?? process.env.STATUS_PUBLIC_URL ?? file.publicUrl,
    region:
      getArgValue(argv, '--region') ?? process.env.AWS_REGION ?? file.region ?? DEFAULT_REGION,
  };
}

// ── AWS credentials ─────────────────────────────────────────────────────────

/** Read base credentials from the environment or `~/.aws/credentials`. */
function loadBaseCredentials(profile?: string): Sv4Credentials {
  if (process.env.AWS_ACCESS_KEY_ID && process.env.AWS_SECRET_ACCESS_KEY) {
    return {
      accessKeyId: process.env.AWS_ACCESS_KEY_ID,
      secretAccessKey: process.env.AWS_SECRET_ACCESS_KEY,
      sessionToken: process.env.AWS_SESSION_TOKEN,
    };
  }
  const name = profile ?? process.env.AWS_PROFILE ?? 'default';
  const creds = parseAwsCredentialsFile(name);
  if (!creds) {
    throw new Error(
      `No AWS credentials found. Set AWS_ACCESS_KEY_ID/AWS_SECRET_ACCESS_KEY or a [${name}] ` +
        `profile in ~/.aws/credentials (or pass --profile).`
    );
  }
  return creds;
}

/** Minimal INI parse of `~/.aws/credentials` for static keys under [profile]. */
function parseAwsCredentialsFile(profile: string): Sv4Credentials | undefined {
  let text: string;
  try {
    text = fs.readFileSync(path.join(os.homedir(), '.aws', 'credentials'), 'utf-8');
  } catch {
    return undefined;
  }
  const lines = text.split('\n');
  let current = '';
  const section: Record<string, string> = {};
  for (const raw of lines) {
    const line = raw.replace(/[;#].*$/, '').trim();
    const header = line.match(/^\[(.+)\]$/);
    if (header) {
      current = header[1].trim();
      continue;
    }
    if (current !== profile) continue;
    const kv = line.match(/^([^=]+)=(.*)$/);
    if (kv) section[kv[1].trim().toLowerCase()] = kv[2].trim();
  }
  if (!section.aws_access_key_id || !section.aws_secret_access_key) return undefined;
  return {
    accessKeyId: section.aws_access_key_id,
    secretAccessKey: section.aws_secret_access_key,
    sessionToken: section.aws_session_token,
  };
}

/** Assume the publisher role via STS, returning 1h temporary credentials. */
async function assumeRole(
  roleArn: string,
  base: Sv4Credentials,
  region: string
): Promise<Sv4Credentials> {
  const host = `sts.${region}.amazonaws.com`;
  const body =
    'Action=AssumeRole&Version=2011-06-15' +
    `&RoleArn=${encodeURIComponent(roleArn)}` +
    '&RoleSessionName=cofactor-status&DurationSeconds=3600';

  const signed = signRequest({
    method: 'POST',
    host,
    path: '/',
    payload: body,
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    service: 'sts',
    region,
    credentials: base,
    amzDate: amzDateNow(),
  });

  const res = await fetch(`https://${host}/`, {
    method: 'POST',
    headers: signed.outgoingHeaders,
    body,
  });
  const xml = await res.text();
  if (!res.ok) {
    throw new Error(`AssumeRole failed (${res.status}): ${extractXml(xml, 'Message') ?? xml.slice(0, 300)}`);
  }
  const accessKeyId = extractXml(xml, 'AccessKeyId');
  const secretAccessKey = extractXml(xml, 'SecretAccessKey');
  const sessionToken = extractXml(xml, 'SessionToken');
  if (!accessKeyId || !secretAccessKey || !sessionToken) {
    throw new Error('AssumeRole response missing credentials');
  }
  return { accessKeyId, secretAccessKey, sessionToken };
}

function extractXml(xml: string, tag: string): string | undefined {
  const m = xml.match(new RegExp(`<${tag}>([^<]*)</${tag}>`));
  return m ? m[1] : undefined;
}

// ── signed write request ────────────────────────────────────────────────────

interface RequestContext {
  writeUrl: string;
  region: string;
  creds: Sv4Credentials;
}

async function buildContext(argv: string[]): Promise<RequestContext> {
  const cfg = resolveConfig(argv);
  if (!cfg.writeUrl) throw new Error('Missing write URL. Pass --write-url, set STATUS_WRITE_URL, or run `cofactor status configure`.');
  if (!cfg.roleArn) throw new Error('Missing role ARN. Pass --role-arn, set STATUS_PUBLISHER_ROLE_ARN, or run `cofactor status configure`.');
  const base = loadBaseCredentials(getArgValue(argv, '--profile'));
  const creds = await assumeRole(cfg.roleArn, base, cfg.region);
  return { writeUrl: cfg.writeUrl.replace(/\/$/, ''), region: cfg.region, creds };
}

async function signedWrite(
  ctx: RequestContext,
  method: string,
  apiPath: string,
  body?: unknown
): Promise<unknown> {
  const url = new URL(ctx.writeUrl + apiPath);
  const payload = body === undefined ? '' : JSON.stringify(body);
  const signed = signRequest({
    method,
    host: url.host,
    path: url.pathname,
    payload,
    headers: payload ? { 'content-type': 'application/json' } : {},
    service: 'lambda',
    region: ctx.region,
    credentials: ctx.creds,
    amzDate: amzDateNow(),
  });
  const res = await fetch(url.toString(), {
    method,
    headers: signed.outgoingHeaders,
    body: payload || undefined,
  });
  const text = await res.text();
  if (!res.ok) {
    throw new Error(`${method} ${apiPath} failed (${res.status}): ${text.slice(0, 400)}`);
  }
  return text ? JSON.parse(text) : {};
}

function splitList(value: string | undefined): string[] {
  return (value ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

// ── command ──────────────────────────────────────────────────────────────────

export async function runStatus(argv: string[]): Promise<void> {
  const sub = argv[0];
  const rest = argv.slice(1);

  if (!sub || sub === 'help' || sub === '--help') {
    printHelp();
    return;
  }

  try {
    switch (sub) {
      case 'configure':
        return runConfigure(rest);
      case 'post':
        return await runPost(rest);
      case 'update':
        return await runUpdate(rest);
      case 'resolve':
        return await runResolve(rest);
      case 'delete':
        return await runDelete(rest);
      case 'component':
        return await runComponent(rest);
      case 'config':
        return await runConfig(rest);
      case 'list':
        return await runList(rest);
      default:
        console.error(`Unknown sub-command "${sub}".`);
        printHelp();
        process.exit(1);
    }
  } catch (err) {
    console.error(err instanceof Error ? err.message : String(err));
    process.exit(1);
  }
}

function runConfigure(argv: string[]): void {
  const current = readConfigFile();
  const next: StatusConfig = {
    writeUrl: getArgValue(argv, '--write-url') ?? current.writeUrl,
    roleArn: getArgValue(argv, '--role-arn') ?? current.roleArn,
    publicUrl: getArgValue(argv, '--public-url') ?? current.publicUrl,
    region: getArgValue(argv, '--region') ?? current.region,
  };
  writeConfigFile(next);
  console.log(`Saved ${CONFIG_PATH}`);
  console.log(JSON.stringify(next, null, 2));
}

async function runPost(argv: string[]): Promise<void> {
  const title = getArgValue(argv, '--title');
  const severity = getArgValue(argv, '--severity');
  const body = getArgValue(argv, '--body');
  if (!title || !severity || !body) {
    throw new Error('Usage: cofactor status post --title <t> --severity <minor|major|critical> --body <text> [--status <s>] [--component a,b]');
  }
  const ctx = await buildContext(argv);
  const payload = {
    title,
    severity,
    body,
    status: getArgValue(argv, '--status') ?? 'investigating',
    affectedComponents: splitList(getArgValue(argv, '--component')),
  };
  const out = await signedWrite(ctx, 'POST', '/api/incidents', payload);
  printResult('Incident created', out);
}

async function runUpdate(argv: string[]): Promise<void> {
  const id = positional(argv);
  if (!id) throw new Error('Usage: cofactor status update <incidentId> [--status <s>] [--body <text>] [--severity <s>]');
  const ctx = await buildContext(argv);
  const payload: Record<string, unknown> = {};
  const status = getArgValue(argv, '--status');
  const body = getArgValue(argv, '--body');
  const severity = getArgValue(argv, '--severity');
  const components = getArgValue(argv, '--component');
  if (status) payload.status = status;
  if (body) payload.body = body;
  if (severity) payload.severity = severity;
  if (components !== undefined) payload.affectedComponents = splitList(components);
  if (Object.keys(payload).length === 0) throw new Error('Nothing to update — pass --status, --body, --severity, or --component.');
  const out = await signedWrite(ctx, 'PUT', `/api/incidents/${encodeURIComponent(id)}`, payload);
  printResult('Incident updated', out);
}

async function runResolve(argv: string[]): Promise<void> {
  const id = positional(argv);
  if (!id) throw new Error('Usage: cofactor status resolve <incidentId> [--body <text>]');
  const ctx = await buildContext(argv);
  const out = await signedWrite(ctx, 'PUT', `/api/incidents/${encodeURIComponent(id)}`, {
    status: 'resolved',
    body: getArgValue(argv, '--body') ?? 'Resolved.',
  });
  printResult('Incident resolved', out);
}

async function runDelete(argv: string[]): Promise<void> {
  const id = positional(argv);
  if (!id) throw new Error('Usage: cofactor status delete <incidentId>');
  const ctx = await buildContext(argv);
  const out = await signedWrite(ctx, 'DELETE', `/api/incidents/${encodeURIComponent(id)}`);
  printResult('Incident deleted', out);
}

async function runComponent(argv: string[]): Promise<void> {
  const action = argv[0];
  const rest = argv.slice(1);
  if (action === 'set') {
    const name = getArgValue(rest, '--name');
    const status = getArgValue(rest, '--status');
    if (!name || !status) throw new Error('Usage: cofactor status component set --name <n> --status <operational|degraded|partial_outage|major_outage|maintenance> [--order <n>] [--id <id>]');
    const ctx = await buildContext(rest);
    const orderRaw = getArgValue(rest, '--order');
    const payload: Record<string, unknown> = { name, status };
    if (getArgValue(rest, '--id')) payload.id = getArgValue(rest, '--id');
    if (orderRaw !== undefined) payload.order = Number(orderRaw);
    const out = await signedWrite(ctx, 'POST', '/api/components', payload);
    printResult('Component set', out);
    return;
  }
  if (action === 'delete') {
    const id = positional(rest);
    if (!id) throw new Error('Usage: cofactor status component delete <componentId>');
    const ctx = await buildContext(rest);
    const out = await signedWrite(ctx, 'DELETE', `/api/components/${encodeURIComponent(id)}`);
    printResult('Component deleted', out);
    return;
  }
  throw new Error('Usage: cofactor status component <set|delete> ...');
}

async function runConfig(argv: string[]): Promise<void> {
  if (argv[0] !== 'set') throw new Error('Usage: cofactor status config set --title <t> [--description <d>]');
  const rest = argv.slice(1);
  const title = getArgValue(rest, '--title');
  if (!title) throw new Error('Usage: cofactor status config set --title <t> [--description <d>]');
  const ctx = await buildContext(rest);
  const out = await signedWrite(ctx, 'PUT', '/api/config', {
    title,
    description: getArgValue(rest, '--description'),
  });
  printResult('Config updated', out);
}

async function runList(argv: string[]): Promise<void> {
  const cfg = resolveConfig(argv);
  if (!cfg.publicUrl) {
    throw new Error('Missing public URL. Pass --public-url, set STATUS_PUBLIC_URL, or run `cofactor status configure --public-url <url>`.');
  }
  const res = await fetch(`${cfg.publicUrl.replace(/\/$/, '')}/api/state`, {
    headers: { accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`Failed to fetch state (${res.status})`);
  const state = (await res.json()) as {
    incidents: Array<{ id: string; title: string; status: string; severity: string }>;
  };
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(state, null, 2));
    return;
  }
  if (state.incidents.length === 0) {
    console.log('No incidents.');
    return;
  }
  for (const i of state.incidents) {
    console.log(`${i.id}  [${i.severity}/${i.status}]  ${i.title}`);
  }
}

function positional(argv: string[]): string | undefined {
  return argv.find((a) => !a.startsWith('--'));
}

function printResult(label: string, out: unknown): void {
  console.log(label);
  console.log(JSON.stringify(out, null, 2));
}

function printHelp(): void {
  console.log(
    [
      'Usage: cofactor status <sub-command> [options]',
      '',
      'Publishes incidents to the self-hosted status page. Authenticates with',
      'AWS IAM: assumes the status-publisher role for short-term credentials and',
      'SigV4-signs the write API. No passwords or API tokens.',
      '',
      'Sub-commands:',
      '  configure   Save write URL / role ARN / public URL to ~/.canary/status.json',
      '  post        Create an incident',
      '  update      Append an update / change status of an incident',
      '  resolve     Mark an incident resolved',
      '  delete      Delete an incident',
      '  component   set | delete a component status',
      '  config      set the page title/description',
      '  list        List incidents (reads the public /api/state)',
      '',
      'Config (each value: --flag > env > ~/.canary/status.json):',
      '  --write-url   | STATUS_WRITE_URL            IAM write Function URL',
      '  --role-arn    | STATUS_PUBLISHER_ROLE_ARN   role to assume',
      '  --public-url  | STATUS_PUBLIC_URL           status page origin (for list)',
      '  --region      | AWS_REGION                  default us-east-1',
      '  --profile     | AWS_PROFILE                 base AWS credentials profile',
      '',
      'Examples:',
      '  cofactor status configure --write-url https://… --role-arn arn:aws:iam::…:role/status-publisher-dev --public-url https://status.runcofactor.com',
      '  cofactor status post --title "DB failover" --severity major --component api,web --body "Investigating"',
      '  cofactor status update inc-… --status monitoring --body "Mitigation deployed"',
      '  cofactor status resolve inc-… --body "Resolved"',
      '  cofactor status component set --name "API" --status operational',
    ].join('\n')
  );
}
